/**
 * ContalinkPendingInvoicesModal - Selección de factura pendiente de registrar en Contalink
 */

import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Invoice } from '../../models/Invoices';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

export interface ContalinkPendingInvoicesModalRef {
  open: (invoices: Invoice[], onSelect: (invoice: Invoice) => void) => void;
}

export const ContalinkPendingInvoicesModal = forwardRef<ContalinkPendingInvoicesModalRef>((_, ref) => {
  const { t } = useTranslation('invoicesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const currency = useCurrency();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [onSelectCallback, setOnSelectCallback] = useState<((invoice: Invoice) => void) | null>(null);

  useImperativeHandle(ref, () => ({
    open: (invoicesList, onSelect) => {
      setInvoices(invoicesList);
      setSelectedId(invoicesList.length === 1 ? invoicesList[0].id : null);
      setOnSelectCallback(() => onSelect);
      modalRef.current?.openModal();
    },
  }));

  const handleClose = () => {
    setInvoices([]);
    setSelectedId(null);
    setOnSelectCallback(null);
  };

  const handleContinue = () => {
    const selected = invoices.find((inv) => inv.id === selectedId);
    if (!selected || !onSelectCallback) return;

    onSelectCallback(selected);
    modalRef.current?.closeModal();
    handleClose();
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

  const selectedInvoice = invoices.find((inv) => inv.id === selectedId);

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.contalink.pending.title', 'Facturas pendientes de Contalink')}
      width="xl"
      onCloseModal={handleClose}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t(
            'modals.contalink.pending.description',
            'Selecciona una factura sin número ERP ni ID de Contalink para completar su registro.',
          )}
        </p>

        <div className="border border-card-border rounded-lg overflow-hidden">
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>{t('table.erp_number', 'Factura')}</TableHead>
                  <TableHead>{t('table.date', 'Fecha')}</TableHead>
                  <TableHead>{t('table.period', 'Período')}</TableHead>
                  <TableHead>{t('filters.teamId', 'Cliente')}</TableHead>
                  <TableHead className="text-right">{t('table.grand_total', 'Total')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    className={`cursor-pointer transition-colors ${selectedId === invoice.id ? 'bg-muted/60' : 'hover:bg-muted/30'}`}
                    onClick={() => setSelectedId(invoice.id)}
                    data-testid={`contalink-pending-row-${invoice.id}`}
                  >
                    <TableCell>
                      <input
                        type="radio"
                        name="contalink-pending-invoice"
                        checked={selectedId === invoice.id}
                        onChange={() => setSelectedId(invoice.id)}
                        className="accent-primary"
                        aria-label={t('modals.contalink.pending.select_invoice', 'Seleccionar factura')}
                      />
                    </TableCell>
                    <TableCell className="font-medium">#{invoice.id}</TableCell>
                    <TableCell>{invoice.date ? formatDate(invoice.date) : '-'}</TableCell>
                    <TableCell>{invoice.period_month || '-'}</TableCell>
                    <TableCell>{invoice.team?.name || '-'}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(invoice.grand_total)} {currency}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {selectedInvoice && (
          <p className="text-sm text-muted-foreground">
            {t('modals.contalink.pending.selected', 'Seleccionada: factura #{{id}} — {{client}}', {
              id: selectedInvoice.id,
              client: selectedInvoice.team?.name || '-',
            })}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-card-border">
          <Button variant="outline" onClick={() => modalRef.current?.closeModal()}>
            {t('modals.contalink.cancel', 'Cancelar')}
          </Button>
          <Button onClick={handleContinue} disabled={!selectedId} data-testid="contalink-pending-continue">
            {t('modals.contalink.pending.continue', 'Continuar')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ContalinkPendingInvoicesModal.displayName = 'ContalinkPendingInvoicesModal';
